import { CurrencyPipe, TitleCasePipe } from '@angular/common';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ErrorHandler, NgModule, provideAppInitializer } from '@angular/core';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';

import { Router } from '@angular/router';
import {
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { getApp, initializeApp, provideFirebaseApp } from '@angular/fire/app';
import {
  getAuth,
  indexedDBLocalPersistence,
  initializeAuth,
  provideAuth,
} from '@angular/fire/auth';
import { getStorage, provideStorage } from '@angular/fire/storage';
import { provideFirestore, initializeFirestore } from '@angular/fire/firestore';
import { provideAnalytics, initializeAnalytics } from '@angular/fire/analytics';
import { GoogleMapsModule } from '@angular/google-maps';
import { IonicModule } from '@ionic/angular';
import { Capacitor } from '@capacitor/core';
import * as Sentry from '@sentry/angular';
import { createErrorHandler } from '@sentry/angular';
import { initializeFaro, getWebInstrumentations } from '@grafana/faro-web-sdk';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';
/* internal dependencies */
import { IonicEquivalentModule } from '@smartflip/util-import-helper';
import { FeaturesAccountDeletionConfirmationModalModule } from '@smartflip/feat-account-deletion-modal';
import { StripePaymentModule, StripePaymentService } from '@smartflip/stripe';
import { FeaturesLoadingIndicatorModule } from '@smartflip/loading-indicator';
import { DataServicesDatafinitiModule } from '@smartflip/datafiniti';
import { ServicesHelperUtilsModule } from '@smartflip/helper-utils';
import { CustomCurrencyPipe, ToThousandsCurrencyPipe } from '@smartflip/pipes';
import { MenuComponent } from '@smart-flip/shared-components';
import { FormFieldsModule } from '@smartflip/form-fields';

import packageJson from './../../package.json';
import { AppRoutingModule } from './modules/app-routing.module';
import { AppComponent } from './app.component';
import { HomeComponent } from './views/home/home.component';

import { LenderFormComponent } from './views/lender/lender-form/lender-form.component';
import { PropertyDetailsModule } from './views/property-details/property-details.module';
import { SharedModule } from './shared/shared.module';
import { environment } from '../environments/environment';
import { of } from 'rxjs';

const faroInitializer = () => {
  if (!Capacitor.isNativePlatform()) {
    return async () => {
      initializeFaro({
        url: environment.faro.url,
        app: {
          name: environment.faro.app.name,
          version: packageJson.version,
          environment: environment.faro.app.environment,
        },
        instrumentations: [
          // Mandatory, omits default instrumentations otherwise.
          ...getWebInstrumentations(),
          // Tracing package to get end-to-end visibility for HTTP requests.
          new TracingInstrumentation(),
        ],
      });
    };
  } else {
    return () => of({});
  }
};

@NgModule({
  providers: [
    provideFirebaseApp(() =>
      initializeApp(environment.firebase, 'The Smart Flip'),
    ),
    provideFirestore(() =>
      initializeFirestore(getApp(), { ignoreUndefinedProperties: true }),
    ),
    provideAnalytics(() => initializeAnalytics(getApp())),
    provideAuth(() =>
      Capacitor.isNativePlatform()
        ? initializeAuth(getApp(), {
            persistence: indexedDBLocalPersistence,
          })
        : getAuth(),
    ),
    provideStorage(() => getStorage()), // only needed for storage features
    CurrencyPipe,
    TitleCasePipe,
    ToThousandsCurrencyPipe,
    ScreenOrientation,
    StripePaymentService,
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: ErrorHandler,
      useValue: createErrorHandler({
        showDialog: true,
      }),
    },
    provideHttpClient(withInterceptorsFromDi()),
    provideAppInitializer(faroInitializer()),
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    IonicEquivalentModule,
    ReactiveFormsModule,
    FormsModule,
    PropertyDetailsModule,
    SharedModule,
    DataServicesDatafinitiModule.forRoot(environment),
    ServicesHelperUtilsModule.forRoot(environment),
    StripePaymentModule.forRoot(environment),
    GoogleMapsModule,
    IonicModule.forRoot(),
    FeaturesLoadingIndicatorModule,
    FeaturesAccountDeletionConfirmationModalModule,
    FormFieldsModule,
  ],
  declarations: [
    AppComponent,
    HomeComponent,
    CustomCurrencyPipe,
    LenderFormComponent,
    MenuComponent,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
